import React from 'react';
import styled from 'styled-components';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {AnimateOnViewWrap, UeLink, WithCaption, WithCaptionProps} from '../../wraps';
import Grid from '@material-ui/core/Grid';
import {ImageProps, Image} from '../images';
import {CmsButtonProps} from '../buttons';
import {GridContainer} from '../grid';
import {ImageAndTextCardLogo, ImageAndTextCardLogoProps} from './ImageAndTextCard.logo';
import {ImageAndTextCardContent} from './ImageAndTextCard.content';
import {VerticalSpacer} from '../spacer';
import {LightboxButtonProps} from '../lightbox/LightboxButton';

const Styled = styled.div<{imageAlignTop?: boolean}>`
    svg {
        font-size: 7.3em;
        ${(p) => p.theme.breakpoints.down('sm')} {
            font-size: 5.7em;
        }
    }
    .paragraph {
        letter-spacing: 0.2px;
    }
    .itc-image-wrap {
        position: relative;
    }
    .ue-link-wrap {
        img {
            transition: transform 0.2s ${(p) => p.theme.easing};
            &:hover {
                transform: scale3d(1.01, 1.01, 1.01);
            }
        }
    }

    .ue-image-and-text-card-img {
        //Always align image with top eyebrow on MD
        ${(p) => p.theme.breakpoints.only('md')} {
            align-self: baseline;
        }
        ${(p) => (p.imageAlignTop ? `align-self: baseline;` : '')}
    }
`;

export interface ImageAndTextCardProps {
    heading?: string;
    title?: string;
    eyebrow?: string;
    image?: ImageProps;
    caption?: WithCaptionProps;
    description?: string;
    accentColor?: AccentColorOptions;
    button?: CmsButtonProps;
    alignment?: 'left' | 'right';
    logo?: ImageAndTextCardLogoProps;
    noGridContainer?: boolean;
    imageAlignTop?: boolean;
    noBottomSpacer?: boolean;
    lightboxButton?: LightboxButtonProps;
    disableAnim?: boolean;
}

export const ImageAndTextCard = (props: ImageAndTextCardProps): JSX.Element => {
    const {
        heading,
        title,
        eyebrow,
        image = {},
        caption = {},
        accentColor = 'sky',
        description,
        button = {},
        alignment = 'left',
        logo = {},
        noGridContainer,
        imageAlignTop,
        noBottomSpacer,
        lightboxButton,
        disableAnim
    } = props;

    const isSm = useBreakpointDown('sm');

    const imageElem = (
        <Grid item xs={12} sm={12} md={8} lg={8} xl={8} className="ue-image-and-text-card-img">
            <div className="itc-image-wrap">
                <UeLink href={button.href} title={heading || title}>
                    <ImageAndTextCardLogo {...logo} alignment={alignment} />
                    <WithCaption {...caption}>
                        <Image {...image} fit="cover" />
                    </WithCaption>
                </UeLink>
            </div>
        </Grid>
    );

    const contentElem = (
        <>
            {logo?.logoImage && isSm && <VerticalSpacer variant="eyebrow" />}
            <ImageAndTextCardContent
                eyebrow={eyebrow}
                heading={heading}
                title={title}
                button={button}
                description={description}
                accentColor={accentColor}
                noBottomSpacer={noBottomSpacer}
                lightboxButton={lightboxButton}
            />
        </>
    );

    let aligmentElems: JSX.Element = <div className="ue-image-and-text-card-no-alignment" />;

    const right = alignment === 'right' || isSm;
    if (right) {
        aligmentElems = (
            <>
                {imageElem}
                {contentElem}
            </>
        );
    } else {
        aligmentElems = (
            <>
                {contentElem}
                {imageElem}
            </>
        );
    }

    const el: JSX.Element = (
        <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
            className={`itc-grid ${right ? 'right' : 'left'}`}
            spacing={isSm ? 0 : 8}
            key={`itc-grid-${heading || title}`}>
            {aligmentElems}
        </Grid>
    );

    return (
        <AnimateOnViewWrap translateY={60} disable={disableAnim}>
            <Styled
                className="ue-image-and-text-card"
                key={`itc-${heading || title}`}
                imageAlignTop={imageAlignTop}>
                {noGridContainer ? el : <GridContainer>{el}</GridContainer>}
            </Styled>
        </AnimateOnViewWrap>
    );
};
